import React, { useMemo } from 'react';

import CustomDamageClass from '../custom-general/custom-damage-class';
import CustomUTMBreakdown from './custom-utm-breakdown';
import CustomVisualInspectionSummary from './custom-utm-summary';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomUTMReportPDF from './custom-utm-report';
import OverviewSheet from '../custom-general/overview-sheet';
import PipingAnalytics from '../custom-visual-inspection/report-items/pipingAnalytics';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface CustomExxonMobilUTMPipingReportProps {
	coverData: models.CustomFVIData;
    reportTitle: string;
    summary: models.CustomUTMPipingSummary;
	anomalySummary: models.AnomalySummary;
    anomalyItems: models.NewAnomalyItem[];
    pidData: models.PIDData;
	generalDrawing: models.PDFImageProps[];
    checklistItems: models.ChecklistItem[];
    checklistAdditionalComment: string;
	photographicData: models.PDFImageProps[];
	projectDetails: models.Project;
	isoImage: models.PDFImageProps[];
	utInspectionItems: models.UTMInspection[];
	inspectionOverview: models.CustomUTMOverview;
}

const CustomExxonMobilUTMPipingReport: React.FC<CustomExxonMobilUTMPipingReportProps> = (props): JSX.Element => {
	const { inspectionTypeName } = props.projectDetails;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

    const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

            <CustomInfoSheet
				projectDetails={props.projectDetails}
                workOrderNumber={props.coverData.workOrderNumber}
                reportTitle={props.reportTitle}
            />

            <OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<CustomDamageClass />

			<ExecutiveSummary summary={props.summary.overview} />

            <CustomVisualInspectionSummary anomalyItems={props.anomalyItems} />

			<CustomUTMBreakdown 
				corrosionAllowance={props.inspectionOverview.corrosionAllowance}
				inspectionItems={props.utInspectionItems} 
			/>

			<PipingAnalytics
				items={[
					{
						hasPDO: 'no',
						pdoChecklist: [],
						hasPID: props.pidData.hasPID,
						lineNumber: props.summary.lineNumber,
						anomalySummary: props.anomalySummary, 
						hasAnomalies: props.summary.hasAnomalies, 
						anomalyItems: props.anomalyItems,
						checklistItems: props.checklistItems,
						checklistAdditionalComment: props.checklistAdditionalComment,
						pIDImages: props.pidData.pIDImages,
						pIDNumber: props.pidData.pIDNumber,
						generalPhotos: props.photographicData,
						generalDrawing: props.generalDrawing,
						anomalyLocations: [],
						id: 1,
						inspectionDescription: 'No anomaly in visual inspection'
					}
				]}
			/>
			
			<CustomUTMReportPDF
				tmlLocations={props.isoImage}
				utInspectionItems={props.utInspectionItems}
				inspectionOverview={props.inspectionOverview}
			/>
		</React.Fragment>
	);
};

export default withPDF(CustomExxonMobilUTMPipingReport);
